import { Injectable } from '@angular/core';
import { logError } from '@office-start/telemetry-service';
import { Observable, Subject } from 'rxjs';

import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { ContextService } from '../shared-context/context.service';
import { GuidGenerator } from '../utilities/guid-generator.utility';
import { OfficeRouteItem, OfficeRouteService } from './office-route.service';
import { Performance } from './performance.service';
import { UpdateContentService } from './update-content.service';

export interface ContentUpdate<T> {
    consecutiveFailures: number;
    cooldown: number;
    lastUpdate: UpdateInfo;
    maxFailures: number;
    operation: () => Promise<T>;
    route: OfficeRouteItem;
    subject: Subject<T>;
    timeout: number;
}

export class UpdateInfo {
    public readonly id: string;
    public readonly startTime: number;
    private endTime?: number;

    constructor(autocomplete: boolean = false) {
        this.id = GuidGenerator.createGuid();
        this.startTime = Performance.now();
        if (autocomplete) {
            this.complete();
        }
    }

    public complete() {
        this.endTime = Performance.now();
    }

    public timeSinceUpdate(): number {
        return this.endTime ? (Performance.now() - this.endTime) : 0;
    }
}

@Injectable()
export class BaseUpdateContentService implements UpdateContentService {

    protected readonly updates: Map<string, ContentUpdate<any>>;
    protected readonly defaultUpdateTimeout: number;
    protected readonly defaultUpdateCooldown: number;
    protected readonly defaultUpdateFailures: number;

    protected static readonly MsPerSecond = 1000;

    constructor(
        contextService: ContextService,
        protected readonly instrumentationService: InstrumentationService,
        protected readonly officeRouteService: OfficeRouteService
    ) {
        this.updates = new Map<string, ContentUpdate<any>>();
        this.defaultUpdateTimeout = contextService.sharedContext.dataUpdateTimeoutInSeconds * BaseUpdateContentService.MsPerSecond;
        this.defaultUpdateCooldown = contextService.sharedContext.dataUpdateCooldownInSeconds * BaseUpdateContentService.MsPerSecond;
        this.defaultUpdateFailures = contextService.sharedContext.dataUpdateMaxConsecutiveFailures;
    }

    public getOrAddUpdateStream<T>(key: string, updateOperation: () => Promise<T>, maxConsecutiveFailures?: number, updateCooldownInMs?: number, updateTimeoutInMs?: number, bypassInitialCooldown: boolean = false): Observable<T> {
        const currentRoute = this.officeRouteService.getRouteItemForCurrentLocation();
        if (!this.updates.has(key)) {
            this.updates.set(key, {
                consecutiveFailures: 0,
                cooldown: updateCooldownInMs || this.defaultUpdateCooldown,
                lastUpdate: bypassInitialCooldown ? null : new UpdateInfo(true),
                maxFailures: maxConsecutiveFailures || this.defaultUpdateFailures,
                operation: updateOperation,
                route: currentRoute,
                subject: new Subject<T>(),
                timeout: updateTimeoutInMs || this.defaultUpdateTimeout
            });
        }

        return this.updates.get(key).subject.asObservable();
    }

    public pushUpdate(key: string): Promise<void> {
        if (!this.updates.has(key)) {
            return Promise.resolve();
        }

        return this.safeUpdate(this.updates.get(key), key);
    }

    protected updateAllowed(update: ContentUpdate<any>): boolean {
        // If no update has been attempted yet, start now
        if (!update.lastUpdate) {
            return true;
        }

        // If the last update has completed, only start a new one after a cooldown
        const timeSinceLastUpdate = update.lastUpdate.timeSinceUpdate();
        if (timeSinceLastUpdate > 0) {
            return timeSinceLastUpdate > update.cooldown;
        }

        // If there is already a pending update, only start a new one if it's lasted a certain time
        return update.timeout && (update.lastUpdate.startTime + update.timeout <= Performance.now());
    }

    protected safeUpdate(update: ContentUpdate<any>, updateKey: string): Promise<void> {
        if (!this.updateAllowed(update)) {
            return Promise.resolve();
        }

        const updateInfo = new UpdateInfo();
        update.lastUpdate = updateInfo;
        return update.operation().then(result => {
            if (updateInfo.id === update.lastUpdate.id) {
                update.subject.next(result);
                update.consecutiveFailures = 0;
            }
        }).catch(error => {
            // Log error - $TODO: Move error-handler.service.ts logic to logError (and subsequently logDiagnostic)
            const errorObject = (error?.error || error);
            const message = `Error message: ${typeof errorObject === 'string' ? errorObject : errorObject?.message || 'not available'}`;
            const debugInfo = `Call stack: ${errorObject?.errorstack || 'not available'}`;

            logError({ eventName: 'BaseUpdateContentServiceError', debugInfo, message });
            this.instrumentationService.logDebugCacheEvent(KnownInstrumentationEvents.BaseUpdateContentServiceError, {
                [InstrumentationParam.Error]: message,
                [InstrumentationParam.DebugInfo]: debugInfo
            });

            // Remove updates after maximum failures
            if (updateInfo.id === update.lastUpdate.id) {
                update.consecutiveFailures++;
                if (update.consecutiveFailures >= update.maxFailures) {
                    // Stops updates for specific content after repeated failures
                    // TODO: Should we support manual reboot somehow?
                    this.updates.delete(updateKey);
                }
            }
        }).then(() => {
            updateInfo.complete();
        });
    }
}
